import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client.js'
import { endpoints } from '../../api/endpoints.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'

/**
 * Platform-admin audit log viewer.
 *
 * Lists every create/update/delete on tracked clinical models. Default
 * row view shows a structured change summary (which field names
 * changed) — never the actual values — so screenshots and screen
 * shares don't leak PHI. Expanding a row shows the full before/after
 * JSON for forensic detail.
 *
 * Filters (all combinable):
 *   • date range (from / to)
 *   • action (created / updated / deleted)
 *   • model (e.g. clinical_notes.Encounter)
 *   • object id
 *   • actor (user id)
 *   • free-text search
 */
export default function AuditLogViewer() {
  const [filters, setFilters] = useState({
    from: '', to: '', action: '', model: '', object_id: '',
    actor: '', q: '',
  })
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState(null)  // row id

  // Build the params object only with non-empty values, plus pagination.
  const params = useMemo(() => {
    const p = { page }
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) p[k] = v
    })
    return p
  }, [filters, page])

  const listQ = useQuery({
    queryKey: ['audit-list', params],
    queryFn: async () => {
      const { data } = await api.get(endpoints.auditLogs, { params })
      return data
    },
  })

  const summaryQ = useQuery({
    queryKey: ['audit-summary', filters],
    queryFn: async () => {
      // Summary uses the same filters but ignores pagination.
      const { page: _p, ...rest } = params
      const { data } = await api.get(endpoints.auditLogsSummary, { params: rest })
      return data
    },
  })

  function setFilter(key, value) {
    setFilters({ ...filters, [key]: value })
    setPage(1)
  }
  function clearFilters() {
    setFilters({ from: '', to: '', action: '', model: '', object_id: '', actor: '', q: '' })
    setPage(1)
  }

  function exportCsv() {
    // Build the same query string the list is using and trigger a
    // browser navigation so the CSV downloads via the response's
    // Content-Disposition header.
    const qs = new URLSearchParams()
    Object.entries(filters).forEach(([k, v]) => { if (v !== '') qs.append(k, v) })
    const url = `${endpoints.auditLogsExport}?${qs.toString()}`
    // Use the api client so the JWT auth header is included.
    api.get(url, { responseType: 'blob' }).then((res) => {
      const blob = new Blob([res.data], { type: 'text/csv' })
      const dl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = dl
      a.download = `audit_log_${new Date().toISOString().slice(0,10)}.csv`
      document.body.appendChild(a); a.click(); a.remove()
      window.URL.revokeObjectURL(dl)
    })
  }

  const rows = listQ.data?.results ?? listQ.data ?? []
  const totalRows = listQ.data?.count ?? rows.length
  const nextPage = listQ.data?.next ? page + 1 : null
  const prevPage = listQ.data?.previous ? page - 1 : null

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Audit log"
          subtitle="Every create, update, and delete on tracked clinical models. Platform-admin only · structured summaries by default · click a row for raw before/after JSON."
          right={
            <button
              onClick={exportCsv}
              className="text-xs font-semibold tracking-wide px-3 py-1.5 rounded-full border border-wantok-300 text-wantok-700 hover:bg-wantok-50 transition-colors"
            >
              ⬇ Export CSV
            </button>
          }
        />
      </Card>

      <SummaryStrip data={summaryQ.data} />

      <FilterPanel
        filters={filters}
        onChange={setFilter}
        onClear={clearFilters}
        topActors={summaryQ.data?.top_actors || []}
        topModels={summaryQ.data?.top_models || []}
      />

      {listQ.error && <ErrorBanner error={listQ.error} onRetry={() => listQ.refetch()} />}
      {listQ.isLoading && (
        <Card><div className="text-sm text-slate-400">Loading audit events…</div></Card>
      )}

      {listQ.data && (
        <Card>
          <CardHeader
            title={`${totalRows.toLocaleString()} event${totalRows === 1 ? '' : 's'}`}
            subtitle="Click any row to expand the raw before/after diff."
          />
          {rows.length === 0 ? (
            <div className="text-sm text-slate-400 italic py-6 text-center">
              No audit events found for this filter.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
                  <tr>
                    <th className="text-left p-2 w-44">When</th>
                    <th className="text-left p-2 w-24">Action</th>
                    <th className="text-left p-2 w-44">Actor</th>
                    <th className="text-left p-2">Model · Object</th>
                    <th className="text-left p-2">Change</th>
                    <th className="text-left p-2 w-24">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <Row
                      key={r.id}
                      row={r}
                      expanded={expanded === r.id}
                      onToggle={() => setExpanded(expanded === r.id ? null : r.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(prevPage || nextPage) && (
            <div className="mt-3 flex items-center justify-end gap-2 text-xs">
              <button
                disabled={!prevPage}
                onClick={() => setPage(page - 1)}
                className="px-3 py-1 border rounded-md text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >← Previous</button>
              <span className="text-slate-500">page {page}</span>
              <button
                disabled={!nextPage}
                onClick={() => setPage(page + 1)}
                className="px-3 py-1 border rounded-md text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >Next →</button>
            </div>
          )}
        </Card>
      )}

      <div className="text-[11px] text-slate-500 italic">
        Audit log is append-only · Records every create/update/delete on
        tracked clinical models · IP and request_id captured for forensic trace.
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
function SummaryStrip({ data }) {
  if (!data) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[0,1,2,3].map((i) => (
          <Card key={i}><div className="h-12 animate-pulse bg-slate-100 rounded" /></Card>
        ))}
      </div>
    )
  }
  const created = data.by_action?.find((b) => b.label === 'Created')?.count ?? 0
  const updated = data.by_action?.find((b) => b.label === 'Updated')?.count ?? 0
  const deleted = data.by_action?.find((b) => b.label === 'Deleted')?.count ?? 0
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <Tile label="Total events" value={data.total ?? 0} sub={`${data.distinct_actors ?? 0} distinct actors · ${data.distinct_models ?? 0} models`} colour="slate" />
      <Tile label="Last 24 hours" value={data.recent_24h ?? 0} sub="recent activity" colour="emerald" />
      <Tile label="Created · Updated" value={`${created.toLocaleString()} · ${updated.toLocaleString()}`} sub="record changes" colour="sky" />
      <Tile label="Deleted" value={deleted} sub="permanent removals" colour="rose" />
    </div>
  )
}

function Tile({ label, value, sub, colour = 'slate' }) {
  const map = {
    slate: 'border-sand-200/60 bg-white',
    emerald: 'border-emerald-200 bg-emerald-50',
    sky: 'border-sky-200 bg-sky-50',
    rose: 'border-rose-200 bg-rose-50',
  }
  return (
    <div className={`border rounded-2xl px-4 py-3 ${map[colour]}`}>
      <div className="text-2xl font-display font-bold text-slate-900 tabular-nums">{value ?? '—'}</div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">{label}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------
function FilterPanel({ filters, onChange, onClear, topActors, topModels }) {
  return (
    <Card>
      <CardHeader title="Filters" subtitle="Combine any. Empty fields are ignored." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Search">
          <input
            type="text"
            value={filters.q}
            onChange={(e) => onChange('q', e.target.value)}
            placeholder="model, object id, ip, request id, actor…"
            className="w-full border rounded-md px-3 py-1.5 text-sm bg-white"
          />
        </Field>
        <Field label="Action">
          <select
            value={filters.action}
            onChange={(e) => onChange('action', e.target.value)}
            className="w-full border rounded-md px-3 py-1.5 text-sm bg-white"
          >
            <option value="">Any</option>
            <option value="created">Created</option>
            <option value="updated">Updated</option>
            <option value="deleted">Deleted</option>
          </select>
        </Field>
        <Field label="Model">
          <input
            type="text"
            list="audit-model-list"
            value={filters.model}
            onChange={(e) => onChange('model', e.target.value)}
            placeholder="e.g. clinical_notes.Encounter — leave empty for all"
            className="w-full border rounded-md px-3 py-1.5 text-sm bg-white"
          />
          <datalist id="audit-model-list">
            {topModels.map((m) => <option key={m.model} value={m.model} />)}
          </datalist>
        </Field>
        <Field label="Object ID">
          <input
            type="text"
            value={filters.object_id}
            onChange={(e) => onChange('object_id', e.target.value)}
            placeholder="e.g. 7"
            className="w-full border rounded-md px-3 py-1.5 text-sm bg-white"
          />
        </Field>
        <Field label="Actor (user id)">
          <input
            type="number"
            list="audit-actor-list"
            value={filters.actor}
            onChange={(e) => onChange('actor', e.target.value)}
            placeholder="e.g. 12"
            className="w-full border rounded-md px-3 py-1.5 text-sm bg-white"
          />
          <datalist id="audit-actor-list">
            {topActors.map((a) => <option key={a.username} value={a.username} />)}
          </datalist>
        </Field>
        <Field label="Date from">
          <input
            type="date"
            value={filters.from}
            onChange={(e) => onChange('from', e.target.value)}
            className="w-full border rounded-md px-3 py-1.5 text-sm bg-white"
          />
        </Field>
        <Field label="Date to">
          <input
            type="date"
            value={filters.to}
            onChange={(e) => onChange('to', e.target.value)}
            className="w-full border rounded-md px-3 py-1.5 text-sm bg-white"
          />
        </Field>
      </div>
      <div className="flex justify-end mt-3">
        <button
          onClick={onClear}
          className="text-xs text-slate-500 hover:text-slate-800 underline"
        >Clear all filters</button>
      </div>
    </Card>
  )
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  )
}

// ---------------------------------------------------------------------------
function Row({ row, expanded, onToggle }) {
  const dt = row.created_at ? new Date(row.created_at) : null
  return (
    <>
      <tr className="hover:bg-slate-50 cursor-pointer" onClick={onToggle}>
        <td className="p-2 tabular-nums text-slate-700">
          {dt ? dt.toLocaleString() : '—'}
        </td>
        <td className="p-2"><ActionBadge action={row.action} /></td>
        <td className="p-2">
          {row.actor_username ? (
            <>
              <div className="font-medium text-slate-800">{row.actor_username}</div>
              {row.actor_role && (
                <div className="text-[10px] uppercase tracking-wide text-slate-400">{row.actor_role.replace('_', ' ')}</div>
              )}
            </>
          ) : (
            <span className="text-slate-400 italic">system</span>
          )}
        </td>
        <td className="p-2 text-slate-700">
          <div className="font-mono text-[12px]">{row.model}</div>
          <div className="text-[11px] text-slate-500">#{row.object_id}</div>
        </td>
        <td className="p-2 text-slate-600">{row.change_summary}</td>
        <td className="p-2 font-mono text-[11px] text-slate-500">{row.ip_address || '—'}</td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50">
          <td colSpan={6} className="p-3">
            <ExpandedDetails row={row} />
          </td>
        </tr>
      )}
    </>
  )
}

function ActionBadge({ action }) {
  const map = {
    created: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    updated: 'bg-sky-50 text-sky-700 border-sky-200',
    deleted: 'bg-rose-50 text-rose-700 border-rose-200',
  }
  const cls = map[action] || 'bg-slate-50 text-slate-600 border-slate-200'
  return (
    <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full border ${cls}`}>
      {action}
    </span>
  )
}

function ExpandedDetails({ row }) {
  const fields = row.changed_fields || []
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <KV k="Request ID" v={row.request_id || '—'} mono />
        <KV k="IP address" v={row.ip_address || '—'} mono />
        <KV k="Object ID" v={`${row.model}#${row.object_id}`} mono />
        <KV k="Recorded" v={row.created_at ? new Date(row.created_at).toLocaleString() : '—'} />
      </div>

      {fields.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
            Changed fields ({fields.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {fields.map((f) => (
              <span key={f} className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-white border border-slate-200">
                {f}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <DiffPanel title="Before" data={row.before_json} colour="rose" />
        <DiffPanel title="After"  data={row.after_json}  colour="emerald" />
      </div>
    </div>
  )
}

function KV({ k, v, mono = false }) {
  return (
    <div className="flex gap-2">
      <span className="text-slate-500 w-24">{k}</span>
      <span className={`text-slate-800 ${mono ? 'font-mono text-[11px]' : ''}`}>{v}</span>
    </div>
  )
}

function DiffPanel({ title, data, colour = 'slate' }) {
  const cls = {
    rose: 'border-rose-200 bg-rose-50',
    emerald: 'border-emerald-200 bg-emerald-50',
  }[colour] || 'border-slate-200 bg-white'
  return (
    <div className={`border rounded-lg ${cls}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 px-3 py-1.5 border-b border-slate-200/70">
        {title}
      </div>
      <pre className="text-[11px] font-mono p-3 max-h-72 overflow-auto whitespace-pre-wrap text-slate-700">
        {data ? JSON.stringify(data, null, 2) : '— (null)'}
      </pre>
    </div>
  )
}
